import { useCallback, useMemo } from 'react';
import { MessageAudienceEnum, MessageAudienceLabelEnum } from '@/enums';
import type { IAdminMessageFormState, ISelectOption } from '@/interfaces';
import { ControlledSelect } from '@/components/controlled';
import { Checkbox, Input, Label } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useFormContext, useWatch } from 'react-hook-form';
import UserMultiSelect from './UserMultiSelect';
import CountryMultiSelect from './CountryMultiSelect';

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Hommes' },
  { value: 'FEMALE', label: 'Femmes' },
] as const;

const RELIGION_OPTIONS = [
  { value: 'CATHOLIC', label: 'Catholique' },
  { value: 'PROTESTANT', label: 'Protestant(e)' },
  { value: 'ISLAM', label: 'Islam' },
  { value: 'HINDU', label: 'Hindou(e)' },
  { value: 'ATHEIST', label: 'Athée' },
  { value: 'CHRISTIAN', label: 'Chrétien(ne)' },
  { value: 'PENTECOSTAL', label: 'Pentecôtiste' },
  { value: 'NO_ANSWER', label: 'Non précisé' },
] as const;

const AGE_MIN = 18;
const AGE_MAX = 60;

type AgeRangeErrors = { min?: { message?: string }; max?: { message?: string } };

const AudienceCard = () => {
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<IAdminMessageFormState>();

  const audience = useWatch({ control, name: 'audience' }) ?? MessageAudienceEnum.ALL;
  const genderFilter = useWatch({ control, name: 'genderFilter', defaultValue: [] });
  const religionFilter = useWatch({ control, name: 'religionFilter', defaultValue: [] });
  const ageRange = useWatch({ control, name: 'ageRange', defaultValue: { min: null, max: null } });

  const isBroadcast = audience !== MessageAudienceEnum.SPECIFIC;

  const audienceOptions = useMemo<ISelectOption[]>(() => [
    { title: MessageAudienceLabelEnum.ALL, value: MessageAudienceEnum.ALL },
    { title: MessageAudienceLabelEnum.ALL_IOS, value: MessageAudienceEnum.ALL_IOS },
    { title: MessageAudienceLabelEnum.ALL_ANDROID, value: MessageAudienceEnum.ALL_ANDROID },
    { title: MessageAudienceLabelEnum.SPECIFIC, value: MessageAudienceEnum.SPECIFIC }
  ], []);

  const toggleGender = useCallback((value: string) => {
    const current = getValues('genderFilter') || [];
    setValue(
      'genderFilter',
      current.includes(value) ? current.filter((g) => g !== value) : [...current, value],
      { shouldDirty: true }
    );
  }, [getValues, setValue]);

  const toggleReligion = useCallback((value: string) => {
    const current = getValues('religionFilter') || [];
    setValue(
      'religionFilter',
      current.includes(value) ? current.filter((r) => r !== value) : [...current, value],
      { shouldDirty: true }
    );
  }, [getValues, setValue]);

  const handleAgeChange = useCallback((field: 'min' | 'max', raw: string) => {
    const fieldPath = `ageRange.${field}` as 'ageRange.min' | 'ageRange.max';
    const siblingPath = (field === 'min' ? 'ageRange.max' : 'ageRange.min') as 'ageRange.min' | 'ageRange.max';

    const parsed = raw === '' ? null : parseInt(raw, 10);
    const value = parsed !== null && !Number.isNaN(parsed) ? parsed : null;
    setValue(fieldPath, value, { shouldDirty: true });

    // Clear both upfront so stale cross-field errors never persist
    clearErrors(fieldPath);
    clearErrors(siblingPath);

    const sibling = getValues(siblingPath);

    if (value !== null) {
      if (value < AGE_MIN || value > AGE_MAX) {
        setError(fieldPath, { type: 'manual', message: `Entre ${AGE_MIN} et ${AGE_MAX} ans` });
      } else if (sibling !== null && (field === 'min' ? value >= sibling : value <= sibling)) {
        setError(fieldPath, { type: 'manual', message: field === 'min' ? 'Min doit être inférieur au max' : 'Max doit être supérieur au min' });
      }
    }

    // Restore sibling's range error if it was out of bounds (cleared above)
    if (sibling !== null && (sibling < AGE_MIN || sibling > AGE_MAX)) {
      setError(siblingPath, { type: 'manual', message: `Entre ${AGE_MIN} et ${AGE_MAX} ans` });
    }
  }, [setValue, clearErrors, setError, getValues]);

  const ageErrors = errors.ageRange as AgeRangeErrors | undefined;
  const minError = ageErrors?.min?.message;
  const maxError = ageErrors?.max?.message;
  const hasAgeError = Boolean(minError || maxError);

  return (
    <div className='hidden md:block bg-white rounded-2xl shadow-soft border border-border'>
      <div className='px-4 py-3 border-b border-border'>
        <h3 className='font-semibold text-sm'>Audience</h3>
      </div>
      <div className='p-4 space-y-4'>
        <ControlledSelect
          control={control}
          name='audience'
          options={audienceOptions}
          placeholder='Sélectionner une audience'
          className='bg-muted/30 focus:bg-background transition-colors h-10'
        />

        {audience === MessageAudienceEnum.SPECIFIC && <UserMultiSelect />}

        {isBroadcast && (
          <div className='space-y-4 pt-1'>
            <div className='border-t border-border' />

            {/* Gender filter */}
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Genre</p>
              <div className='flex gap-4'>
                {GENDER_OPTIONS.map(({ value, label }) => (
                  <Label
                    key={value}
                    className='flex items-center gap-2 cursor-pointer text-sm font-normal'
                  >
                    <Checkbox
                      checked={genderFilter.includes(value)}
                      onCheckedChange={() => toggleGender(value)}
                    />
                    {label}
                  </Label>
                ))}
              </div>
              {genderFilter.length === 0 && (
                <p className='text-[11px] text-muted-foreground'>Tous les genres inclus</p>
              )}
            </div>

            {/* Religion filter */}
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Religion</p>
              <div className='grid grid-cols-2 gap-x-4 gap-y-1.5'>
                {RELIGION_OPTIONS.map(({ value, label }) => (
                  <Label
                    key={value}
                    className='flex items-center gap-2 cursor-pointer text-sm font-normal'
                  >
                    <Checkbox
                      checked={religionFilter.includes(value)}
                      onCheckedChange={() => toggleReligion(value)}
                    />
                    {label}
                  </Label>
                ))}
              </div>
              {religionFilter.length === 0 && (
                <p className='text-[11px] text-muted-foreground'>Toutes les religions incluses</p>
              )}
            </div>

            {/* Age range filter */}
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Tranche d'âge</p>
              <div className='flex items-center gap-2'>
                <Input
                  type='number'
                  min={AGE_MIN}
                  max={AGE_MAX}
                  placeholder='Min'
                  value={ageRange.min ?? ''}
                  onChange={(e) => handleAgeChange('min', e.target.value)}
                  className={cn(
                    'h-8 w-20 text-sm bg-muted/30 focus:bg-background transition-colors',
                    minError && 'border-red-500 focus-visible:ring-transparent'
                  )}
                />
                <span className='text-muted-foreground text-xs'>–</span>
                <Input
                  type='number'
                  min={AGE_MIN}
                  max={AGE_MAX}
                  placeholder='Max'
                  value={ageRange.max ?? ''}
                  onChange={(e) => handleAgeChange('max', e.target.value)}
                  className={cn(
                    'h-8 w-20 text-sm bg-muted/30 focus:bg-background transition-colors',
                    maxError && 'border-red-500 focus-visible:ring-transparent'
                  )}
                />
                <span className='text-xs text-muted-foreground'>ans</span>
              </div>

              {hasAgeError ? (
                <p className='text-[11px] text-red-500'>{minError ?? maxError}</p>
              ) : (ageRange.min != null || ageRange.max != null) ? (
                <p className='text-[11px] text-muted-foreground'>
                  {ageRange.min != null && ageRange.max != null
                    ? `${ageRange.min}–${ageRange.max} ans`
                    : ageRange.min != null
                      ? `${ageRange.min} ans et plus`
                      : `${ageRange.max} ans et moins`}
                </p>
              ) : null}
            </div>

            {/* Country filter */}
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Pays</p>
              <CountryMultiSelect showLabel={false} />
            </div>

            {/* Excluded users */}
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Utilisateurs exclus</p>
              <UserMultiSelect name='excludedUsers' showLabel={false} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudienceCard;
